import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  requiredArrayValidator,
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createB2BDocument,
  getAllB2BDocument,
  getB2BDocumentByID,
  updateB2BDocument,
} from "./services/b2bDocument.services";
import { clearB2BDocumentDetails } from "./b2bDocumentSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { STATUSARRAY } from "../../_contstants/common";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import FormFileUpload from "../../components/formFields/FormFileUpload";
import IconComponent from "../../components/common/IconComponent";
import { FaNoteSticky } from "react-icons/fa6";

const CreateB2BDocument: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const B2BDocumentDetail = useAppSelector(
    (state) => state.b2bDocument.B2BDocumentDetail,
  );
  const B2BDocumentID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.b2bDocument.formLoading);

  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    return () => {
      dispatch(clearB2BDocumentDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [B2BDocumentDetail]);

  useEffect(() => {
    if (B2BDocumentID) {
      dispatch(getB2BDocumentByID(B2BDocumentID));
    }
  }, [B2BDocumentID]);

  const handleSubmit = async (values: any) => {
    const formData = new FormData();
    formData.append("Title", values?.Title);
    if (values?.Document && values?.Document[0]) {
      formData.append("Image", values?.Document[0]?.getRawFile());
    }
    formData.append("IsActive", String(values?.IsActive === 1));
    try {
      if (!B2BDocumentID) {
        const response = await dispatch(createB2BDocument(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearB2BDocumentDetails());
          dispatch(getAllB2BDocument());
        }
      } else {
        formData.append("ID", B2BDocumentID);
        const response = await dispatch(updateB2BDocument(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearB2BDocumentDetails());
          dispatch(getAllB2BDocument());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        initialValues={B2BDocumentDetail}
        key={formKey}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {B2BDocumentID ? "Update Document " : "Add Document"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="Title"
                  label="Title"
                  placeholder="Document Title"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  width: "100%",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Field
                    wrapperClassName="w-100"
                    name="Document"
                    type="file"
                    filetype="file"
                    label="Document"
                    accept=".pdf, .doc, .docx, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    multiple={false}
                    component={FormFileUpload}
                    validator={
                      B2BDocumentID ? skipValidator : requiredArrayValidator
                    }
                    astrike={B2BDocumentID ? false : true}
                  />
                </div>
                {/* Streamlined eye icon beside field if document is available */}
                {(() => {
                  const localDoc = formRenderProps.valueGetter("Document");
                  const hasLocalDoc =
                    Array.isArray(localDoc) && localDoc.length > 0;
                  const hasExistingDoc = !!B2BDocumentDetail?.Image;

                  if (hasLocalDoc || hasExistingDoc) {
                    const handleOpenDoc = () => {
                      if (hasLocalDoc) {
                        try {
                          const rawFile = localDoc[0].getRawFile();
                          const objectUrl = URL.createObjectURL(rawFile);
                          window.open(objectUrl, "_blank");
                        } catch (err) {
                          console.error("Error opening uploaded file:", err);
                        }
                      } else if (hasExistingDoc) {
                        window.open(B2BDocumentDetail.Image, "_blank");
                      }
                    };

                    const docName = hasLocalDoc
                      ? localDoc[0].name
                      : B2BDocumentDetail.Image || "";
                    const isPdf = docName.toLowerCase().endsWith(".pdf");

                    return (
                      <button
                        type="button"
                        onClick={handleOpenDoc}
                        title={`View ${isPdf ? "PDF" : "Document"} in New Tab`}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "10px",
                          marginTop: "31px",
                          borderRadius: "50%",
                          transition: "all 0.2s ease-in-out",
                          color: isPdf ? "#e11d48" : "#2563eb",
                          backgroundColor: isPdf ? "#fff1f2" : "#eff6ff",
                          borderWidth: "1px",
                          borderStyle: "solid",
                          borderColor: isPdf ? "#fecdd3" : "#bfdbfe",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.1)";
                          e.currentTarget.style.backgroundColor = isPdf
                            ? "#ffe4e6"
                            : "#dbeafe";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.backgroundColor = isPdf
                            ? "#fff1f2"
                            : "#eff6ff";
                        }}
                      >
                        <IconComponent icon={FaNoteSticky} />
                      </button>
                    );
                  }
                  return null;
                })()}
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  placeholder="i.e. Active"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  label={"Status"}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "start",
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={B2BDocumentID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateB2BDocument;
